import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormControlStatus, FormGroup } from '@angular/forms';
import {
    DataMartDataModelResponse,
    DataMartTable,
    DivisionConfig,
    DivisionTableRelationships,
    SalesforceListFieldResponse,
    SalesforceObjectMetadata,
    TableRelationship,
} from '@r1-compass-library/models';
import { combineLatest, forkJoin, map, Observable, of, tap } from 'rxjs';
import { FormValueOf } from '@r1-compass-library/utilities';
import { filterNil } from 'ngxtension/filter-nil';
import { DataMartService } from '@r1-compass-library/services';

export type DivisionSettingsFormSliceState<T> = {
    // Form stuff
    value: T;
    status: FormControlStatus;
    loaded: boolean;
    // Neccisary data from APIs
    dataMartTableOptions: DataMartTable[];
    recommendationFieldsOptions: SalesforceListFieldResponse | undefined;
    salesforceObjectOptions: SalesforceObjectMetadata[];
    selectedDivisionConfig: DivisionConfig | undefined;
};

/**
 * @description Basic Settings + Salesforce Targets + Data Mapping shared functionality
 * They used to all be in one shared form service with a single `signalSlice`, one huge model file,
 * and one huge validators file. Now they have their own files/services, but they function similarly.
 * This service ensures the core lifecycle of the signal slice form init and save process is the same.
 */
@Injectable({ providedIn: 'root' })
export class FormLifecycleService {
    readonly #dataMartService = inject(DataMartService);

    public enableOrDisableWhenLoading(loaded: boolean, form: FormGroup) {
        // Initialize
        if (!loaded) {
            form.disable({ emitEvent: false });
        }

        // Enable after load
        if (loaded) {
            form.enable({ emitEvent: false });
            form.updateValueAndValidity();
        }
    }

    public initializeAndPatchForm<T extends { [K in keyof T]: AbstractControl }>(
        selectedDivisionConfig$: Observable<DivisionConfig | undefined>,
        loaded$: Observable<boolean>,
        form: FormGroup<T>,
        initializeControls: (data: DivisionConfig) => void,
        mapDivisionConfigToFormData: (data: DivisionConfig) => {
            value: Partial<FormValueOf<FormGroup<T>>>;
            status: FormControlStatus;
            loaded: boolean;
        }
    ): Observable<{
        loaded: boolean;
    }> {
        return combineLatest([selectedDivisionConfig$.pipe(filterNil()), loaded$]).pipe(
            // 1) Create empty form controls for X mapping fields of whatever type
            tap(([data, loaded]) => {
                if (!loaded) {
                    initializeControls(data);
                }
            }),
            // 2) Shape data to form slice state, and mark `loaded: true`
            map(([division, _]) => {
                return mapDivisionConfigToFormData(division);
            }),
            // 3) Patch form values that were mapped
            tap(data => {
                if (data.loaded) {
                    form.patchValue(data.value);
                    form.updateValueAndValidity();
                }
            }),
            map(() => {
                // 4) Still need the slice to return something
                return { loaded: true };
            })
        );
    }

    /**
     * @description Initialize datamart data slice
     */
    public configureDataMartData(selectedDiv: DivisionConfig | undefined, dataModel: DataMartDataModelResponse) {
        const dataMartTableOptions = dataModel.tables;
        const dataMartTable = dataMartTableOptions.find(
            table => table.name === selectedDiv?.config?.mapping?.dataMartTable
        );
        const dataMartFields =
            dataMartTableOptions.find(
                table =>
                    table.name ===
                    dataMartTableOptions.find(tbl => tbl.name === selectedDiv?.config?.mapping?.dataMartTable)?.name
            )?.fields ?? [];

        return {
            dataMartTable,
            dataMartTableOptions,
            dataMartFields,
        };
    }

    getRelationsAndCrtieriaTypes(
        uniqueSfTargetFieldsLength: number,
        sourceFieldName: string[],
        dataMartTableName: string,
        getDataMartRelations: (
            sourceTableName: string,
            sourceColumnName: string
        ) => Observable<DivisionTableRelationships | undefined>
    ) {
        const relations$ = this.setRelations(
            uniqueSfTargetFieldsLength,
            sourceFieldName,
            dataMartTableName,
            getDataMartRelations
        );

        const criteria$ = this.#dataMartService.getSegmentationCriteria();

        const dataMartCalls$ = forkJoin([relations$, criteria$]);

        const relationsAndCriteriaTypes$ = dataMartCalls$.pipe(
            map(([rels, crit]) => {
                let relationships = rels.filter(
                    (relation): relation is DivisionTableRelationships => relation !== undefined
                );
                relationships = relationships.length > 0 ? relationships : [];

                const criteriaTypes = crit?.segmentationCriteria ? crit.segmentationCriteria : [];
                return {
                    relationships: relationships,
                    criteriaTypes: criteriaTypes,
                };
            })
        );

        return relationsAndCriteriaTypes$;
    }

    private setRelations(
        uniqueSfTargetFieldsLength: number,
        sourceFieldName: string[],
        dataMartTableName: string,
        getDataMartRelations: (
            sourceTableName: string,
            sourceColumnName: string
        ) => Observable<DivisionTableRelationships | undefined>
    ) {
        const relations: Pick<TableRelationship, 'sourceTableName' | 'sourceColumnName'>[] = (
            new Array(uniqueSfTargetFieldsLength).fill('filler-to-iterate-over') ?? []
        ).map((_, i) => ({
            sourceTableName: dataMartTableName,
            sourceColumnName: sourceFieldName[i],
        }));

        const relations$ =
            relations.length > 0
                ? forkJoin(
                      relations.map(relation => {
                          return getDataMartRelations(relation.sourceTableName, relation.sourceColumnName);
                      })
                  )
                : of([]);

        return relations$;
    }
}
